import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Calendar,
  CheckCircle2,
  Clock,
  Send,
  Users,
  Eye,
  Lock,
  CalendarOff,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { format, addDays, getDay } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';
import { SHIFT_TYPE_KEYS, normaliseRanking, prefLabel, visibleRanking, mergeRanking } from '../constants/shiftTypes';
import VacationRangePicker from '../components/common/VacationRangePicker';
import { freeWeekends, freeSaturdaySet, formatWeekends, formatAssignedAt } from '../constants/weekendRhythm';
import {
  rangeEndStr, formatVacationRange, rangesOverlap, normaliseFormEntry, formEntryKey,
} from '../constants/vacationRange';

// ── Constants ─────────────────────────────────────────────────────────────────


const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const ORANGE = '#f97316';
const PURPLE = '#7c3aed';
const PURPLE_LIGHT = '#ede9fe';
const ORANGE_LIGHT = '#fff7ed';

// ── Pure helpers ──────────────────────────────────────────────────────────────

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = (getDay(first) + 6) % 7; // Mon = 0
  const grid = Array(startPad).fill(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(year, month, d));
  return grid;
}

function getMonthsInPeriod(startStr, endStr) {
  const end = new Date(endStr + 'T12:00:00');
  const months = [];
  let d = new Date(new Date(startStr + 'T12:00:00').getFullYear(), new Date(startStr + 'T12:00:00').getMonth(), 1);
  while (d <= end) {
    months.push({ year: d.getFullYear(), month: d.getMonth() });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return months;
}

// Vacation entries of the form: { start_date, length_days } (legacy { week_start } → 7 days)
function normaliseVacationEntries(list) {
  return (list || []).map(normaliseFormEntry).filter(Boolean);
}


function initPrefs(raw) {
  return {
    // Rankings are shift_type keys; legacy label rankings are converted
    ranking: normaliseRanking(raw?.ranking),
    include_pms: raw?.include_pms || false,
    pms_dates: raw?.pms_dates || [],
  };
}

// ── WindowBanner ──────────────────────────────────────────────────────────────

function WindowBanner({ activeWindow, submitted }) {
  if (!activeWindow) return null;
  const { deadline, days_remaining, opened_at, submitted_count, total_users } = activeWindow;
  const daysOpen = Math.max(0, Math.floor((Date.now() - new Date(opened_at)) / 86400000));
  const total = daysOpen + days_remaining;
  const progress = total > 0 ? Math.min(100, Math.round((daysOpen / total) * 100)) : 100;
  const barColor = days_remaining <= 2 ? 'var(--danger)' : days_remaining <= 5 ? 'var(--warning)' : 'var(--primary)';

  return (
    <div
      style={{
        background: submitted ? 'var(--success-light)' : 'var(--primary-xlight)',
        border: `1px solid ${submitted ? 'var(--success)' : 'var(--primary)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '16px 20px',
        marginBottom: '20px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {submitted
            ? <CheckCircle2 size={18} style={{ color: 'var(--success)', flexShrink: 0 }} />
            : <Clock size={18} style={{ color: 'var(--primary-dark)', flexShrink: 0 }} />}
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {submitted ? 'Availability submitted' : 'Availability collection is open'}
            </p>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '2px' }}>
              Deadline: {deadline} · {days_remaining} day{days_remaining !== 1 ? 's' : ''} remaining
            </p>
          </div>
        </div>
        {submitted_count !== undefined && (
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            {submitted_count}/{total_users} submitted
          </span>
        )}
      </div>
      <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.08)', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
        <div style={{ width: `${progress}%`, height: '100%', background: barColor, borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ── FullDayMonth ──────────────────────────────────────────────────────────────
// Red marks, weekdays only

function FullDayMonth({ year, month, periodStart, periodEnd, markedDates, onToggle, readOnly }) {
  const grid = buildMonthGrid(year, month);
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const periodStartDate = new Date(periodStart + 'T12:00:00');
  const periodEndDate = new Date(periodEnd + 'T12:00:00');
  const markedSet = new Set(markedDates);
  let monthCount = 0;
  for (const d of markedDates) { if (d.startsWith(monthKey)) monthCount++; }
  const overLimit = monthCount > 4;

  return (
    <div style={{ minWidth: '196px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <p style={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {format(new Date(year, month, 1), 'MMMM yyyy')}
        </p>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: overLimit ? 'var(--danger)' : 'var(--text-muted)' }}>
          {monthCount}/4 full days
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {DAY_LABELS.map((h) => (
          <div key={h} style={{ textAlign: 'center', fontSize: '0.63rem', fontWeight: 600, color: 'var(--text-muted)', paddingBottom: '4px' }}>
            {h}
          </div>
        ))}
        {grid.map((date, i) => {
          if (!date) return <div key={`pad-${i}`} />;
          const dateStr = format(date, 'yyyy-MM-dd');
          const inPeriod = date >= periodStartDate && date <= periodEndDate;
          const isMarked = markedSet.has(dateStr);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const disabled = !inPeriod || isWeekend;
          return (
            <button
              key={dateStr}
              onClick={() => !readOnly && !disabled && onToggle(dateStr)}
              title={isWeekend && inPeriod ? 'Weekend — full-day blocks must be Mon–Fri' : dateStr}
              style={{
                padding: '3px 0',
                border: 'none',
                borderRadius: '4px',
                cursor: readOnly || disabled ? (isWeekend && inPeriod ? 'not-allowed' : 'default') : 'pointer',
                fontSize: '0.72rem',
                fontWeight: isMarked ? 700 : 400,
                background: isMarked ? 'var(--danger)' : 'transparent',
                color: !inPeriod
                  ? 'var(--border)'
                  : isMarked
                  ? '#fff'
                  : isWeekend
                  ? 'var(--border)'
                  : 'var(--text)',
                opacity: isWeekend && inPeriod ? 0.35 : 1,
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── FullDayBlocksSection ──────────────────────────────────────────────────────

function FullDayBlocksSection({ period, unavailableDates, halfDayDates, onChange, readOnly }) {
  const months = getMonthsInPeriod(period.start_date, period.end_date);
  const halfSet = new Set(halfDayDates);

  function toggleDate(dateStr) {
    if (halfSet.has(dateStr)) {
      toast.error('Remove the half-day block for this date first.');
      return;
    }
    if (unavailableDates.includes(dateStr)) {
      onChange(unavailableDates.filter((d) => d !== dateStr));
    } else {
      onChange([...unavailableDates, dateStr].sort());
    }
  }

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Full Day Blocks</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
          {readOnly
            ? 'Full days marked unavailable (Mon–Fri).'
            : 'Mon–Fri only · max 4 per month · click to toggle.'}
        </p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
        {months.map(({ year, month }) => (
          <FullDayMonth
            key={`${year}-${month}`}
            year={year}
            month={month}
            periodStart={period.start_date}
            periodEnd={period.end_date}
            markedDates={unavailableDates}
            onToggle={toggleDate}
            readOnly={readOnly}
          />
        ))}
      </div>
      {unavailableDates.length > 0 && (
        <p style={{ marginTop: '12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {unavailableDates.length} full day{unavailableDates.length !== 1 ? 's' : ''} blocked
        </p>
      )}
    </div>
  );
}

// ── HalfDayMonth ──────────────────────────────────────────────────────────────
// Orange AM/PM marks, popup on click

function HalfDayMonth({ year, month, periodStart, periodEnd, halfDayBlocks, fullDayDates, onUpdate, readOnly }) {
  const [popup, setPopup] = useState(null); // { dateStr }
  const containerRef = useRef(null);

  const grid = buildMonthGrid(year, month);
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const periodStartDate = new Date(periodStart + 'T12:00:00');
  const periodEndDate = new Date(periodEnd + 'T12:00:00');

  const blockMap = {};
  for (const { date, part } of halfDayBlocks) {
    if (date.startsWith(monthKey)) blockMap[date] = part;
  }
  const fullSet = new Set(fullDayDates);

  let monthCount = 0;
  for (const { date } of halfDayBlocks) { if (date.startsWith(monthKey)) monthCount++; }
  const overLimit = monthCount > 4;

  const handleChoice = useCallback((choice) => {
    if (!popup) return;
    const { dateStr } = popup;
    setPopup(null);
    if (choice === 'remove') {
      onUpdate(halfDayBlocks.filter((b) => b.date !== dateStr));
    } else {
      const filtered = halfDayBlocks.filter((b) => b.date !== dateStr);
      onUpdate([...filtered, { date: dateStr, part: choice }]);
    }
  }, [popup, halfDayBlocks, onUpdate]);

  // Close popup on outside click
  useEffect(() => {
    if (!popup) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPopup(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [popup]);

  return (
    <div style={{ minWidth: '196px', position: 'relative' }} ref={containerRef}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <p style={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {format(new Date(year, month, 1), 'MMMM yyyy')}
        </p>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: overLimit ? 'var(--danger)' : 'var(--text-muted)' }}>
          {monthCount}/4 half days
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {DAY_LABELS.map((h) => (
          <div key={h} style={{ textAlign: 'center', fontSize: '0.63rem', fontWeight: 600, color: 'var(--text-muted)', paddingBottom: '4px' }}>
            {h}
          </div>
        ))}
        {grid.map((date, i) => {
          if (!date) return <div key={`pad-${i}`} />;
          const dateStr = format(date, 'yyyy-MM-dd');
          const inPeriod = date >= periodStartDate && date <= periodEndDate;
          const isFullDay = fullSet.has(dateStr);
          const halfPart = blockMap[dateStr];
          const isActive = popup?.dateStr === dateStr;

          return (
            <button
              key={dateStr}
              onClick={() => {
                if (readOnly || !inPeriod || isFullDay) return;
                setPopup(isActive ? null : { dateStr });
              }}
              title={isFullDay && inPeriod ? 'Full-day block — remove it first' : dateStr}
              style={{
                padding: '2px 0',
                border: isActive ? `2px solid ${ORANGE}` : '2px solid transparent',
                borderRadius: '4px',
                cursor: readOnly || !inPeriod || isFullDay ? (isFullDay && inPeriod ? 'not-allowed' : 'default') : 'pointer',
                fontSize: '0.65rem',
                fontWeight: halfPart ? 700 : 400,
                background: isFullDay && inPeriod
                  ? 'var(--border)'
                  : halfPart
                  ? ORANGE
                  : 'transparent',
                color: !inPeriod
                  ? 'var(--border)'
                  : isFullDay
                  ? 'var(--text-muted)'
                  : halfPart
                  ? '#fff'
                  : 'var(--text)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1.2,
                minHeight: '28px',
              }}
            >
              <span>{date.getDate()}</span>
              {halfPart && (
                <span style={{ fontSize: '0.55rem', fontWeight: 700, opacity: 0.9 }}>
                  {halfPart === 'morning' ? 'AM' : 'PM'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Inline popup */}
      {popup && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 100,
          background: 'var(--white)',
          border: `1px solid ${ORANGE}`,
          borderRadius: 'var(--radius)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          padding: '8px',
          minWidth: '200px',
          marginTop: '4px',
        }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 600 }}>
            {popup.dateStr}
          </p>
          {['morning', 'afternoon'].map((part) => (
            <button
              key={part}
              onClick={() => handleChoice(part)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                minHeight: '44px',
                border: `1px solid ${blockMap[popup.dateStr] === part ? ORANGE : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                background: blockMap[popup.dateStr] === part ? ORANGE_LIGHT : 'transparent',
                color: blockMap[popup.dateStr] === part ? ORANGE : 'var(--text)',
                fontWeight: blockMap[popup.dateStr] === part ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.88rem',
                marginBottom: '4px',
              }}
            >
              {part === 'morning' ? 'Morning (before 13:00)' : 'Afternoon (after 13:00)'}
            </button>
          ))}
          {blockMap[popup.dateStr] && (
            <button
              onClick={() => handleChoice('remove')}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                minHeight: '44px',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius)',
                background: 'transparent',
                color: 'var(--danger)',
                cursor: 'pointer',
                fontSize: '0.88rem',
              }}
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── HalfDayBlocksSection ──────────────────────────────────────────────────────

function HalfDayBlocksSection({ period, halfDayBlocks, fullDayDates, onChange, readOnly }) {
  const months = getMonthsInPeriod(period.start_date, period.end_date);

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Half Day Blocks</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
          {readOnly
            ? 'Half days blocked (AM = before 13:00, PM = after 13:00).'
            : 'Any day · max 4 per month · tap a date to choose morning or afternoon.'}
        </p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
        {months.map(({ year, month }) => (
          <HalfDayMonth
            key={`half-${year}-${month}`}
            year={year}
            month={month}
            periodStart={period.start_date}
            periodEnd={period.end_date}
            halfDayBlocks={halfDayBlocks}
            fullDayDates={fullDayDates}
            onUpdate={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
      {halfDayBlocks.length > 0 && (
        <p style={{ marginTop: '12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {halfDayBlocks.length} half day{halfDayBlocks.length !== 1 ? 's' : ''} blocked
        </p>
      )}
    </div>
  );
}

// ── WeekendMonth ──────────────────────────────────────────────────────────────
// Purple weekend blocks; clicking Sat or Sun toggles the pair

function WeekendMonth({ year, month, periodStart, periodEnd, blockedSaturdaySet, onToggleSat, readOnly, rhythmMode }) {
  const grid = buildMonthGrid(year, month);
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const periodStartDate = new Date(periodStart + 'T12:00:00');
  const periodEndDate = new Date(periodEnd + 'T12:00:00');

  let monthCount = 0;
  for (const sat of blockedSaturdaySet) { if (sat.startsWith(monthKey)) monthCount++; }
  const overLimit = !rhythmMode && monthCount > 2;

  // For a given date, find its Saturday date string
  function getSaturdayOf(date) {
    const dow = date.getDay(); // 0=Sun, 6=Sat
    if (dow === 6) return format(date, 'yyyy-MM-dd');
    if (dow === 0) return format(addDays(date, -1), 'yyyy-MM-dd');
    return null;
  }

  return (
    <div style={{ minWidth: '196px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <p style={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {format(new Date(year, month, 1), 'MMMM yyyy')}
        </p>
        {!rhythmMode && (
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: overLimit ? 'var(--danger)' : 'var(--text-muted)' }}>
            {monthCount}/2 weekends
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {DAY_LABELS.map((h) => (
          <div key={h} style={{ textAlign: 'center', fontSize: '0.63rem', fontWeight: 600, color: 'var(--text-muted)', paddingBottom: '4px' }}>
            {h}
          </div>
        ))}
        {grid.map((date, i) => {
          if (!date) return <div key={`pad-${i}`} />;
          const dateStr = format(date, 'yyyy-MM-dd');
          const inPeriod = date >= periodStartDate && date <= periodEndDate;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const satStr = getSaturdayOf(date);
          const isBlocked = satStr ? blockedSaturdaySet.has(satStr) : false;
          const isClickable = !readOnly && inPeriod && isWeekend && satStr;

          return (
            <button
              key={dateStr}
              onClick={() => isClickable && onToggleSat(satStr)}
              title={dateStr}
              style={{
                padding: '3px 0',
                border: 'none',
                borderRadius: '4px',
                cursor: isClickable ? 'pointer' : 'default',
                fontSize: '0.72rem',
                fontWeight: isBlocked && isWeekend ? 700 : 400,
                background: isBlocked && isWeekend && inPeriod ? PURPLE : 'transparent',
                color: !inPeriod
                  ? 'var(--border)'
                  : isBlocked && isWeekend
                  ? '#fff'
                  : isWeekend
                  ? 'var(--text-muted)'
                  : 'var(--text)',
                opacity: !isWeekend && inPeriod ? 0.4 : 1,
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── WeekendBlocksSection ──────────────────────────────────────────────────────

// Rhythm in effect for one submission: historical A/B, else the assigned one
function resolvedRhythm(weekendRhythm, assignedRhythm) {
  if (weekendRhythm === 'A' || weekendRhythm === 'B') return weekendRhythm;
  if (weekendRhythm === 'flexible') return assignedRhythm || null;
  return null;
}

// Calendar preview of the free weekends of a rhythm (read-only)
function RhythmPreview({ period, rhythm }) {
  const months = getMonthsInPeriod(period.start_date, period.end_date);
  const freeSet = freeSaturdaySet(rhythm, period.start_date, period.end_date);
  return (
    <>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
        Preview — purple weekends are off:
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
        {months.map(({ year, month }) => (
          <WeekendMonth
            key={`rhythm-${year}-${month}`}
            year={year}
            month={month}
            periodStart={period.start_date}
            periodEnd={period.end_date}
            blockedSaturdaySet={freeSet}
            onToggleSat={() => {}}
            readOnly
            rhythmMode
          />
        ))}
      </div>
    </>
  );
}

function WeekendBlocksSection({ period, blockedWeekends, weekendRhythm, weekendMode, readOnly, onChange, assignedRhythm, assignedAt }) {
  const months = getMonthsInPeriod(period.start_date, period.end_date);
  const blockedSaturdaySet = new Set(blockedWeekends);

  function handleModeSwitch(newMode) {
    if (newMode === weekendMode) return;
    const hasData = weekendMode === 'specific'
      ? blockedWeekends.length > 0
      : weekendRhythm !== null;
    if (newMode === 'specific' && hasData && !window.confirm('Switching will clear your current weekend settings. Continue?')) return;
    if (newMode === 'rhythm' && blockedWeekends.length > 0 && !window.confirm('Switching will clear your blocked weekends. Continue?')) return;
    onChange({
      blocked_weekends: [],
      weekend_rhythm: newMode === 'rhythm' ? 'flexible' : null,
      mode: newMode,
    });
  }

  function toggleSat(satStr) {
    const next = blockedSaturdaySet.has(satStr)
      ? blockedWeekends.filter((d) => d !== satStr)
      : [...blockedWeekends, satStr].sort();
    onChange({ blocked_weekends: next, weekend_rhythm: null, mode: 'specific' });
  }

  const rhythm = resolvedRhythm(weekendRhythm, assignedRhythm);
  const historical = weekendRhythm === 'A' || weekendRhythm === 'B';

  const modeRadioStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    border: `1px solid ${active ? PURPLE : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    background: active ? PURPLE_LIGHT : 'transparent',
    cursor: readOnly ? 'default' : 'pointer',
    marginBottom: '8px',
    fontSize: '0.88rem',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Weekend Availability</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
          {readOnly ? 'Weekend block settings.' : 'Choose how you handle weekends.'}
        </p>
      </div>

      {/* Mode selector */}
      {!readOnly && (
        <div style={{ marginBottom: '16px' }}>
          <button
            style={modeRadioStyle(weekendMode === 'specific')}
            onClick={() => handleModeSwitch('specific')}
          >
            <div style={{
              width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${weekendMode === 'specific' ? PURPLE : 'var(--border)'}`,
              background: weekendMode === 'specific' ? PURPLE : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {weekendMode === 'specific' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
            </div>
            <div>
              <div>Block specific weekends</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>Max 2 per month</div>
            </div>
          </button>
          <button
            style={modeRadioStyle(weekendMode === 'rhythm')}
            onClick={() => handleModeSwitch('rhythm')}
          >
            <div style={{
              width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${weekendMode === 'rhythm' ? PURPLE : 'var(--border)'}`,
              background: weekendMode === 'rhythm' ? PURPLE : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {weekendMode === 'rhythm' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
            </div>
            <div>
              <div>Every other weekend off — the system chooses</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                {assignedRhythm ? `You keep Rhythm ${assignedRhythm}` : 'Assigned when the plan is generated, then kept'}
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Specific mode */}
      {weekendMode === 'specific' && (
        <>
          {readOnly && blockedWeekends.length === 0 && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No weekends blocked.</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
            {months.map(({ year, month }) => (
              <WeekendMonth
                key={`wknd-${year}-${month}`}
                year={year}
                month={month}
                periodStart={period.start_date}
                periodEnd={period.end_date}
                blockedSaturdaySet={blockedSaturdaySet}
                onToggleSat={toggleSat}
                readOnly={readOnly}
              />
            ))}
          </div>
          {blockedWeekends.length > 0 && (
            <p style={{ marginTop: '12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {blockedWeekends.length} weekend{blockedWeekends.length !== 1 ? 's' : ''} blocked
            </p>
          )}
        </>
      )}

      {/* Rhythm mode */}
      {weekendMode === 'rhythm' && (
        <>
          {readOnly && (
            <p style={{ fontSize: '0.88rem', marginBottom: '8px' }}>
              <strong>Every other weekend off</strong>{historical ? ' (earlier choice)' : ' — the system chooses'}
            </p>
          )}
          {rhythm ? (
            <div style={{
              padding: '10px 14px', marginBottom: '14px', borderRadius: 'var(--radius)',
              background: 'var(--primary-xlight)', border: '1px solid var(--primary)', fontSize: '0.86rem',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Your rhythm: Rhythm {rhythm}
                {!historical && assignedAt ? ` (since ${formatAssignedAt(assignedAt)})` : ''}
              </div>
              <div>
                Your free weekends in this plan:{' '}
                {formatWeekends(freeWeekends(rhythm, period.start_date, period.end_date)) || 'none'}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
              The system assigns your free weekends when the plan is generated and keeps the rhythm for later plans.
            </p>
          )}
          {rhythm && <RhythmPreview period={period} rhythm={rhythm} />}
        </>
      )}
    </div>
  );
}

// ── VacationSection ───────────────────────────────────────────────────────────

const VACATION_STATUS_STYLE = {
  pending:  { label: 'Pending',  bg: 'var(--warning-light)', color: '#b45309' },
  approved: { label: 'Approved', bg: 'var(--success-light)', color: '#2E7D32' },
  rejected: { label: 'Rejected', bg: 'var(--danger-light)',  color: 'var(--danger)' },
};

function VacationStatusBadge({ status }) {
  const st = VACATION_STATUS_STYLE[status];
  if (!st) return null;
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
      background: st.bg, color: st.color, whiteSpace: 'nowrap',
    }}>
      {st.label}
    </span>
  );
}

function VacationSection({ period, vacationWeeks, statusByWeek = {}, balance, onChange, readOnly, allowPast }) {
  const [draft, setDraft] = useState({ start_date: '', length_days: 7 });
  const entries = normaliseVacationEntries(vacationWeeks)
    .slice()
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const minDate = allowPast || tomorrow < period.start_date ? period.start_date : tomorrow;
  const draftRange = draft.start_date
    ? { start_date: draft.start_date, end_date: rangeEndStr(draft.start_date, draft.length_days) }
    : null;
  const clash = draftRange && entries.find((e) => rangesOverlap(
    { start_date: e.start_date, end_date: rangeEndStr(e.start_date, e.length_days) }, draftRange,
  ));
  const outOfPlan = draftRange && (draft.start_date < minDate || draft.start_date > period.end_date);

  function addEntry() {
    if (!draftRange || clash || outOfPlan) return;
    onChange([...entries, { start_date: draft.start_date, length_days: draft.length_days }]);
    setDraft({ start_date: '', length_days: 7 });
  }

  function removeEntry(key) {
    onChange(entries.filter((e) => formEntryKey(e) !== key));
  }

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Vacation Requests</h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            {readOnly
              ? 'Requested vacation.'
              : 'Vacation is taken in whole weeks (7, 14, 21 or 28 days) and can start on any day of this plan. Each entry is sent as a vacation request; only approved vacation is kept free in the roster.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--primary-xlight)', border: '1px solid var(--primary)', borderRadius: 'var(--radius)', padding: '6px 12px' }}>
          <Calendar size={14} style={{ color: 'var(--primary-dark)' }} />
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{balance} day{balance !== 1 ? 's' : ''} remaining</span>
        </div>
      </div>

      {!readOnly && (
        <div style={{ marginBottom: entries.length ? '16px' : 0 }}>
          <VacationRangePicker
            startDate={draft.start_date}
            lengthDays={draft.length_days}
            min={minDate}
            max={period.end_date}
            onChange={setDraft}
          />
          {clash && (
            <p style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: '6px' }}>
              Overlaps {formatVacationRange(clash.start_date, rangeEndStr(clash.start_date, clash.length_days))}.
            </p>
          )}
          {outOfPlan && (
            <p style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: '6px' }}>
              The start date must lie within this plan{allowPast ? '' : ' and in the future'}.
            </p>
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: '10px' }}
            onClick={addEntry}
            disabled={!draftRange || !!clash || outOfPlan}
          >
            Add vacation
          </button>
        </div>
      )}

      {entries.length === 0 ? (
        readOnly && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No vacation requested.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {entries.map((e) => {
            const key = formEntryKey(e);
            return (
              <div
                key={key}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                  padding: '10px 14px', border: '1px solid var(--primary)',
                  borderRadius: 'var(--radius)', background: 'var(--primary-xlight)',
                }}
              >
                <span style={{ fontSize: '0.88rem', flex: '1 1 200px', minWidth: 0 }}>
                  {formatVacationRange(e.start_date, rangeEndStr(e.start_date, e.length_days))}
                </span>
                <VacationStatusBadge status={statusByWeek[key]} />
                {!readOnly && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ padding: '3px 10px', fontSize: '0.78rem' }}
                    onClick={() => removeEntry(key)}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── PmsDaysSection ────────────────────────────────────────────────────────────

function PmsDaysSection({ period, prefs, onChange, readOnly }) {
  const [expanded, setExpanded] = useState(prefs.include_pms);
  const pmsCount = prefs.pms_dates?.length || 0;

  function toggleDate(dateStr) {
    const current = prefs.pms_dates || [];
    const isMarked = current.includes(dateStr);
    if (!isMarked && current.length >= 4) return;
    const updated = isMarked
      ? current.filter((d) => d !== dateStr)
      : [...current, dateStr].sort();
    onChange({ ...prefs, pms_dates: updated });
  }

  function toggleSection(val) {
    setExpanded(val);
    onChange({ ...prefs, include_pms: val, pms_dates: val ? prefs.pms_dates : [] });
  }

  const months = getMonthsInPeriod(period.start_date, period.end_date);
  const periodStartDate = new Date(period.start_date + 'T12:00:00');
  const periodEndDate = new Date(period.end_date + 'T12:00:00');
  const markedSet = new Set(prefs.pms_dates || []);

  return (
    <div className="card" style={{ marginBottom: '16px', borderStyle: expanded ? 'solid' : 'dashed' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            PMS / Cycle Days <Lock size={12} style={{ color: 'var(--text-muted)' }} />
          </h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Optional — only visible to you and admins. Max 4 days, shown in dark blue.
          </p>
        </div>
        {!readOnly && (
          <button
            onClick={() => toggleSection(!expanded)}
            className="btn btn-secondary btn-sm"
          >
            {expanded ? 'Hide' : 'Add PMS dates'}
          </button>
        )}
        {readOnly && expanded && (
          <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>Private</span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
            {months.map(({ year, month }) => {
              const grid = buildMonthGrid(year, month);
              const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
              let monthCount = 0;
              for (const d of (prefs.pms_dates || [])) { if (d.startsWith(monthKey)) monthCount++; }
              return (
                <div key={`pms-${year}-${month}`} style={{ minWidth: '196px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {format(new Date(year, month, 1), 'MMMM yyyy')}
                    </p>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                      {pmsCount}/4
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                    {DAY_LABELS.map((h) => (
                      <div key={h} style={{ textAlign: 'center', fontSize: '0.63rem', fontWeight: 600, color: 'var(--text-muted)', paddingBottom: '4px' }}>
                        {h}
                      </div>
                    ))}
                    {grid.map((date, i) => {
                      if (!date) return <div key={`pad-${i}`} />;
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const inPeriod = date >= periodStartDate && date <= periodEndDate;
                      const isMarked = markedSet.has(dateStr);
                      return (
                        <button
                          key={dateStr}
                          onClick={() => !readOnly && inPeriod && toggleDate(dateStr)}
                          style={{
                            padding: '3px 0',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: readOnly || !inPeriod ? 'default' : 'pointer',
                            fontSize: '0.72rem',
                            fontWeight: isMarked ? 700 : 400,
                            background: isMarked ? '#1A5276' : 'transparent',
                            color: !inPeriod ? 'var(--border)' : isMarked ? '#fff' : 'var(--text)',
                          }}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: pmsCount >= 4 ? '#1A5276' : 'var(--text-muted)', fontWeight: pmsCount >= 4 ? 600 : 400 }}>
              {pmsCount} / 4 PMS days marked
            </span>
            {pmsCount >= 4 && !readOnly && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Maximum of 4 days reached</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PreferenceRanking ─────────────────────────────────────────────────────────

function PreferenceRanking({ ranking, onChange, readOnly, hiddenCount = 0 }) {
  function moveUp(i) {
    if (i === 0) return;
    const list = [...ranking];
    [list[i - 1], list[i]] = [list[i], list[i - 1]];
    onChange(list);
  }
  function moveDown(i) {
    if (i === ranking.length - 1) return;
    const list = [...ranking];
    [list[i], list[i + 1]] = [list[i + 1], list[i]];
    onChange(list);
  }

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Shift Preference Ranking</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
          {readOnly ? 'Shift preferences (1 = most preferred).' : 'Use the arrows to rank — 1 is most preferred.'}
        </p>
        {hiddenCount > 0 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
            Only the shift types you can be scheduled for are shown.
          </p>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {ranking.map((type, i) => (
          <div
            key={type}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 14px',
              border: i === 0 ? '1px solid var(--primary)' : '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              background: i === 0 ? 'var(--primary-xlight)' : 'var(--bg)',
            }}
          >
            <span style={{
              width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
              background: i === 0 ? 'var(--primary-dark)' : 'var(--border)',
              color: i === 0 ? '#fff' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.7rem', fontWeight: 700,
            }}>
              {i + 1}
            </span>
            <span style={{ flex: 1, fontSize: '0.9rem' }}>{prefLabel(type)}</span>
            {i === 0 && <span style={{ fontSize: '0.7rem', color: 'var(--primary-dark)', fontWeight: 600 }}>Most preferred</span>}
            {i === ranking.length - 1 && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Least preferred</span>}
            {!readOnly && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                <button
                  aria-label="Move up"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  style={{
                    width: '36px', height: '36px', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', background: 'var(--white)',
                    cursor: i === 0 ? 'default' : 'pointer',
                    opacity: i === 0 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', lineHeight: 1,
                  }}
                >▲</button>
                <button
                  aria-label="Move down"
                  onClick={() => moveDown(i)}
                  disabled={i === ranking.length - 1}
                  style={{
                    width: '36px', height: '36px', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', background: 'var(--white)',
                    cursor: i === ranking.length - 1 ? 'default' : 'pointer',
                    opacity: i === ranking.length - 1 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', lineHeight: 1,
                  }}
                >▼</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── UserSubmissionModal ───────────────────────────────────────────────────────

const REC_DAY_NAMES_AVAIL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const REC_PART_LABELS_AVAIL = {
  full: 'full day',
  morning: 'morning (before 13:00)',
  afternoon: 'afternoon (after 13:00)',
};

function recValidityStrAvail(b) {
  if (b.valid_from && b.valid_until) return ` · ${b.valid_from} – ${b.valid_until}`;
  if (b.valid_from) return ` · from ${b.valid_from}`;
  if (b.valid_until) return ` · until ${b.valid_until}`;
  return '';
}

function UserSubmissionModal({ user, avail, period, onClose }) {
  const prefs = avail?.shift_preferences || {};
  const months = period ? getMonthsInPeriod(period.start_date, period.end_date) : [];
  const halfDayBlocks = avail?.half_day_blocks || [];
  const blockedWeekends = avail?.blocked_weekends || [];
  const weekendRhythm = avail?.weekend_rhythm || null;
  const weekendMode = weekendRhythm ? 'rhythm' : 'specific';

  const [recurringBlocks, setRecurringBlocks] = useState([]);
  useEffect(() => {
    api.get(`/users/${user.id}/recurring-blocks`)
      .then((r) => setRecurringBlocks(r.data))
      .catch(() => {});
  }, [user.id]);

  // Weekend rhythm: historical A/B, or the system-assigned rhythm (also kept
  // without a submission)
  const assigned = avail?.weekend_rhythm_assigned ?? user.weekend_rhythm_assigned ?? null;
  const assignedAt = avail?.weekend_rhythm_assigned_at ?? user.weekend_rhythm_assigned_at ?? null;
  const rhythm = avail ? resolvedRhythm(weekendRhythm, assigned) : assigned;
  const blockedSaturdaySet = new Set(blockedWeekends);
  let rhythmText = null;
  if (!avail) {
    rhythmText = assigned ? `No submission — keeps Rhythm ${assigned}.` : null;
  } else if (weekendRhythm === 'A' || weekendRhythm === 'B') {
    rhythmText = `Weekend rhythm: Rhythm ${weekendRhythm} (earlier choice).`;
  } else if (weekendRhythm === 'flexible') {
    rhythmText = assigned
      ? `Weekend rhythm: let the system choose → currently Rhythm ${assigned}${assignedAt ? ` (assigned ${formatAssignedAt(assignedAt)})` : ''}.`
      : 'Weekend rhythm: let the system choose → not assigned yet (decided at next generation).';
  }

  return (
    <Modal isOpen onClose={onClose} title={`${user.name}'s Availability`} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 14px', background: 'var(--bg)', borderRadius: 'var(--radius)', fontSize: '0.78rem' }}>
          {[
            { color: 'var(--danger)', label: 'Full day blocked' },
            { color: ORANGE, label: 'Half day (AM/PM)' },
            { color: PURPLE, label: 'Weekend blocked' },
            { color: '#1A5276', label: 'PMS day' },
          ].map(({ color, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, flexShrink: 0 }} />
              {label}
            </span>
          ))}
        </div>

        {/* Full day blocks */}
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '10px' }}>
            Full Day Blocks ({avail?.unavailable_dates?.length || 0})
          </p>
          {avail?.unavailable_dates?.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
              {months.map(({ year, month }) => (
                <FullDayMonth
                  key={`fd-${year}-${month}`}
                  year={year} month={month}
                  periodStart={period.start_date} periodEnd={period.end_date}
                  markedDates={avail.unavailable_dates}
                  onToggle={() => {}} readOnly
                />
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>None</p>
          )}
        </div>

        {/* Half day blocks */}
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '10px' }}>
            Half Day Blocks ({halfDayBlocks.length})
          </p>
          {halfDayBlocks.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
              {months.map(({ year, month }) => (
                <HalfDayMonth
                  key={`hd-${year}-${month}`}
                  year={year} month={month}
                  periodStart={period.start_date} periodEnd={period.end_date}
                  halfDayBlocks={halfDayBlocks}
                  fullDayDates={avail?.unavailable_dates || []}
                  onUpdate={() => {}}
                  readOnly
                />
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>None</p>
          )}
        </div>

        {/* Weekend blocks */}
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '10px' }}>
            Weekend Blocks
          </p>
          {rhythmText ? (
            <>
              <p style={{ fontSize: '0.85rem', marginBottom: '6px' }}>{rhythmText}</p>
              {rhythm && (
                <>
                  <p style={{ fontSize: '0.85rem', marginBottom: '10px' }}>
                    Free weekends in this plan:{' '}
                    {formatWeekends(freeWeekends(rhythm, period.start_date, period.end_date)) || 'none'}
                  </p>
                  <RhythmPreview period={period} rhythm={rhythm} />
                </>
              )}
            </>
          ) : weekendMode === 'specific' && blockedWeekends.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
              {months.map(({ year, month }) => (
                <WeekendMonth
                  key={`wknd-${year}-${month}`}
                  year={year} month={month}
                  periodStart={period.start_date} periodEnd={period.end_date}
                  blockedSaturdaySet={blockedSaturdaySet}
                  onToggleSat={() => {}} readOnly
                />
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>None</p>
          )}
        </div>

        {/* Vacation weeks */}
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>
            Vacation ({avail?.vacation_week_requests?.length || 0})
          </p>
          {avail?.vacation_week_requests?.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {normaliseVacationEntries(avail.vacation_week_requests).map((e) => (
                <span key={formEntryKey(e)} style={{
                  background: 'var(--primary-xlight)', border: '1px solid var(--primary)',
                  borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '0.82rem',
                }}>
                  {formatVacationRange(e.start_date, rangeEndStr(e.start_date, e.length_days))}{' '}
                  <VacationStatusBadge status={avail.vacation_week_status?.[formEntryKey(e)]} />
                </span>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>None</p>
          )}
        </div>

        {/* PMS dates */}
        {prefs.pms_dates?.length > 0 && (
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              PMS / Cycle Days <Lock size={11} style={{ color: 'var(--text-muted)' }} />
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
              {months.map(({ year, month }) => {
                const periodStartDate = new Date(period.start_date + 'T12:00:00');
                const periodEndDate = new Date(period.end_date + 'T12:00:00');
                const markedSet = new Set(prefs.pms_dates);
                const grid = buildMonthGrid(year, month);
                return (
                  <div key={`pms-${year}-${month}`} style={{ minWidth: '196px' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>
                      {format(new Date(year, month, 1), 'MMMM yyyy')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                      {DAY_LABELS.map((h) => (
                        <div key={h} style={{ textAlign: 'center', fontSize: '0.63rem', fontWeight: 600, color: 'var(--text-muted)', paddingBottom: '4px' }}>{h}</div>
                      ))}
                      {grid.map((date, i) => {
                        if (!date) return <div key={`pad-${i}`} />;
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const inPeriod = date >= periodStartDate && date <= periodEndDate;
                        const isMarked = markedSet.has(dateStr);
                        return (
                          <div key={dateStr} style={{
                            padding: '3px 0', textAlign: 'center',
                            borderRadius: '4px', fontSize: '0.72rem', fontWeight: isMarked ? 700 : 400,
                            background: isMarked ? '#1A5276' : 'transparent',
                            color: !inPeriod ? 'var(--border)' : isMarked ? '#fff' : 'var(--text)',
                          }}>
                            {date.getDate()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ranking */}
        {prefs.ranking?.length > 0 && (
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>Shift Preference Ranking</p>
            {visibleRanking(prefs.ranking, avail?.qualified_shift_types).map((type, i) => (
              <div key={type} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 12px', marginBottom: '4px',
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', fontSize: '0.88rem',
              }}>
                <span style={{
                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  background: i === 0 ? 'var(--primary-dark)' : 'var(--border)',
                  color: i === 0 ? '#fff' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {i + 1}
                </span>
                {prefLabel(type)}
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        {avail?.period_notes && (
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '6px' }}>Notes</p>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', fontStyle: 'italic' }}>
              "{avail.period_notes}"
            </p>
          </div>
        )}

        {/* Recurring blocks (admin-set) */}
        {recurringBlocks.length > 0 && (
          <div style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--radius)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            <p style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text)', fontSize: '0.82rem' }}>
              Recurring blocks (admin-set)
            </p>
            {recurringBlocks.map((b) => (
              <div key={b.id} style={{ lineHeight: 1.6 }}>
                Every {REC_DAY_NAMES_AVAIL[b.weekday]} · {REC_PART_LABELS_AVAIL[b.part]}{recValidityStrAvail(b)}{b.note ? ` · ${b.note}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── AdminOverviewPanel ────────────────────────────────────────────────────────

function AdminOverviewPanel({ period, statusList, activeWindow, allSubmissions, onOpenWindow, onSendReminder }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const pending = statusList.filter((u) => !u.submitted);
  const userAvail = selectedUser ? allSubmissions.find((a) => a.user_id === selectedUser.id) || null : null;

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Team Submissions</h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            <Users size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
            {statusList.filter((u) => u.submitted).length}/{statusList.length} submitted
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!activeWindow ? (
            <button className="btn btn-primary btn-sm" onClick={onOpenWindow} disabled={!period}>
              <Calendar size={14} /> Open Collection Window
            </button>
          ) : (
            <button className="btn btn-secondary btn-sm" onClick={onSendReminder} disabled={pending.length === 0}>
              <Send size={14} /> Remind {pending.length} pending
            </button>
          )}
        </div>
      </div>

      {statusList.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No team members found.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {statusList.map((u) => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              background: u.submitted ? 'var(--bg)' : 'transparent',
              gap: '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, background: u.submitted ? 'var(--success)' : 'var(--border)' }} />
                <div>
                  <p style={{ fontSize: '0.88rem', fontWeight: 500 }}>{u.name}</p>
                  {u.submitted_at && (
                    <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                      {u.submitted_at.slice(0, 10)}
                    </p>
                  )}
                  {!u.submitted && u.weekend_rhythm_assigned && (
                    <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                      No submission — keeps Rhythm {u.weekend_rhythm_assigned}
                    </p>
                  )}
                </div>
              </div>
              {u.submitted ? (
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedUser(u)}>
                  <Eye size={13} /> View
                </button>
              ) : (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Pending</span>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedUser && (
        <UserSubmissionModal
          user={selectedUser}
          avail={userAvail}
          period={period}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

// ── AvailabilityPage ──────────────────────────────────────────────────────────

// Default plan when no valid ?period= is given: the plan whose collection window
// is open; otherwise the nearest upcoming plan; otherwise the most recent one.
function pickDefaultPeriodId(periods, activeWindows) {
  if (activeWindows.length > 0) return activeWindows[0].period.id;
  if (periods.length === 0) return null;
  const today = format(new Date(), 'yyyy-MM-dd');
  const upcoming = periods
    .filter((p) => p.start_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  if (upcoming.length > 0) return upcoming[0].id;
  return [...periods].sort((a, b) => b.start_date.localeCompare(a.start_date))[0].id;
}

function AvailabilityPage() {
  const { isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodParam = searchParams.get('period');

  // Admins who don't work shifts only manage the team overview — no personal form
  const showOwnForm = !isAdmin || user?.works_shifts !== 0;

  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [myAvail, setMyAvail] = useState(null);
  const [activeWindows, setActiveWindows] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);

  const [localDates, setLocalDates] = useState([]);
  const [localWeeks, setLocalWeeks] = useState([]);
  const [localPrefs, setLocalPrefs] = useState({ ranking: [...SHIFT_TYPE_KEYS], include_pms: false, pms_dates: [] });
  // Shift types this user may be scheduled for — the ranking shows only these
  // (the stored ranking keeps all keys; the server ignores the others)
  const [myQualified, setMyQualified] = useState(null);
  useEffect(() => {
    api.get('/auth/me').then((res) => setMyQualified(res.data?.qualified_shift_types || null)).catch(() => {});
  }, []);
  const [localNotes, setLocalNotes] = useState('');
  const [localHalfDays, setLocalHalfDays] = useState([]);
  const [localBlockedWeekends, setLocalBlockedWeekends] = useState([]);
  const [localWeekendRhythm, setLocalWeekendRhythm] = useState(null);
  const [weekendMode, setWeekendMode] = useState('specific');
  const [dirty, setDirty] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initial load: periods + active windows
  useEffect(() => {
    Promise.all([
      api.get('/shifts/roster-periods').catch(() => null),
      api.get('/availabilities/windows/active').catch(() => null),
    ]).then(([pRes, wRes]) => {
      const pds = pRes?.data || [];
      const wins = wRes?.data || [];

      const periodIds = new Set(pds.map((p) => p.id));
      const merged = [...pds];
      for (const win of wins) {
        if (!periodIds.has(win.period.id)) merged.push(win.period);
      }

      setPeriods(merged);
      setActiveWindows(wins);

      // Deep link (?period=<id>) wins if it refers to a plan this user can see
      const requested = Number(new URLSearchParams(window.location.search).get('period'));
      const initialId = merged.some((p) => p.id === requested)
        ? requested
        : pickDefaultPeriodId(merged, wins);

      if (initialId) {
        setSelectedPeriodId(initialId);
      } else {
        setPageLoading(false);
      }
    });
  }, []);

  // URL → selection (e.g. back/forward, or opening another deep link while on the page)
  useEffect(() => {
    const id = Number(periodParam);
    if (id && id !== selectedPeriodId && periods.some((p) => p.id === id)) {
      setSelectedPeriodId(id);
    }
  }, [periodParam, periods]);

  // Selection → URL, so the current plan can be bookmarked or shared
  useEffect(() => {
    if (!selectedPeriodId || String(selectedPeriodId) === periodParam) return;
    const next = new URLSearchParams(searchParams);
    next.set('period', String(selectedPeriodId));
    setSearchParams(next, { replace: true });
  }, [selectedPeriodId]);

  // Load per-period data when selectedPeriodId changes
  useEffect(() => {
    if (!selectedPeriodId) return;
    setPeriodLoading(true);
    const fetches = [
      showOwnForm
        ? api.get(`/availabilities/me?period_id=${selectedPeriodId}`).catch(() => null)
        : Promise.resolve(null),
    ];
    if (isAdmin) {
      fetches.push(api.get(`/availabilities/status?period_id=${selectedPeriodId}`).catch(() => null));
      fetches.push(api.get(`/availabilities?period_id=${selectedPeriodId}`).catch(() => null));
    }
    Promise.all(fetches).then(([meRes, statusRes, allRes]) => {
      if (meRes) {
        const avail = meRes.data;
        setMyAvail(avail);
        setLocalDates(avail.unavailable_dates || []);
        setLocalWeeks(normaliseVacationEntries(avail.vacation_week_requests));
        setLocalPrefs(initPrefs(avail.shift_preferences));
        setLocalNotes(avail.period_notes || '');
        setLocalHalfDays(avail.half_day_blocks || []);
        const rhythm = avail.weekend_rhythm || null;
        setLocalWeekendRhythm(rhythm);
        setLocalBlockedWeekends(avail.blocked_weekends || []);
        setWeekendMode(rhythm ? 'rhythm' : 'specific');
      }
      if (statusRes) setStatusList(statusRes.data || []);
      if (allRes) setAllSubmissions(allRes.data || []);
      setDirty(false);
      setPageLoading(false);
      setPeriodLoading(false);
    });
  }, [selectedPeriodId, isAdmin, showOwnForm]);

  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId) || null;
  const activeWindow = activeWindows.find((w) => w.period.id === selectedPeriodId) || null;
  const canEdit = isAdmin || !!activeWindow;

  function markDirty(setter) {
    setter();
    setDirty(true);
  }

  async function handleSubmit() {
    if (!selectedPeriodId) return;
    setSaving(true);
    const body = {
      roster_period_id: selectedPeriodId,
      unavailable_dates: localDates,
      vacation_week_requests: localWeeks,
      shift_preferences: localPrefs,
      period_notes: localNotes || null,
      half_day_blocks: localHalfDays,
      blocked_weekends: weekendMode === 'specific' ? localBlockedWeekends : [],
      weekend_rhythm: weekendMode === 'rhythm' ? localWeekendRhythm : null,
    };
    try {
      if (myAvail?.id) {
        await api.put(`/availabilities/${myAvail.id}`, body);
      } else {
        await api.post('/availabilities', body);
      }
      toast.success(myAvail?.id ? 'Availability updated.' : 'Availability submitted.');

      const [meRes, wRes, statusRes, allRes] = await Promise.all([
        api.get(`/availabilities/me?period_id=${selectedPeriodId}`),
        api.get('/availabilities/windows/active').catch(() => null),
        isAdmin ? api.get(`/availabilities/status?period_id=${selectedPeriodId}`).catch(() => null) : Promise.resolve(null),
        isAdmin ? api.get(`/availabilities?period_id=${selectedPeriodId}`).catch(() => null) : Promise.resolve(null),
      ]);
      setMyAvail(meRes.data);
      if (wRes) setActiveWindows(wRes.data);
      if (statusRes) setStatusList(statusRes.data || []);
      if (allRes) setAllSubmissions(allRes.data || []);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenWindow() {
    if (!selectedPeriodId) return;
    try {
      const res = await api.post('/availabilities/windows', { period_id: selectedPeriodId });
      toast.success(res.data.message);
      const wRes = await api.get('/availabilities/windows/active').catch(() => null);
      if (wRes) setActiveWindows(wRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to open window.');
    }
  }

  async function handleSendReminder() {
    if (!selectedPeriodId) return;
    try {
      const res = await api.post('/availabilities/windows/remind', { period_id: selectedPeriodId });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reminder.');
    }
  }

  function handleDiscard() {
    if (!myAvail) return;
    setLocalDates(myAvail.unavailable_dates || []);
    setLocalWeeks(normaliseVacationEntries(myAvail.vacation_week_requests));
    setLocalPrefs(initPrefs(myAvail.shift_preferences));
    setLocalNotes(myAvail.period_notes || '');
    setLocalHalfDays(myAvail.half_day_blocks || []);
    const rhythm = myAvail.weekend_rhythm || null;
    setLocalWeekendRhythm(rhythm);
    setLocalBlockedWeekends(myAvail.blocked_weekends || []);
    setWeekendMode(rhythm ? 'rhythm' : 'specific');
    setDirty(false);
  }

  if (pageLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Availability</h1>
          <p className="page-subtitle">
            {showOwnForm
              ? 'Submit your availability for the upcoming roster period.'
              : 'Team overview: collection windows and submissions per roster plan.'}
          </p>
        </div>
        {periods.length > 1 && (
          <select
            value={selectedPeriodId || ''}
            onChange={(e) => setSelectedPeriodId(Number(e.target.value))}
            className="form-select"
            style={{ minWidth: 0, width: '100%', maxWidth: '280px' }}
            aria-label="Roster plan"
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.start_date} – {p.end_date}
              </option>
            ))}
          </select>
        )}
      </div>

      {periods.length === 0 ? (
        <div className="empty-state">
          <CalendarOff className="empty-state-icon" />
          <h3>{isAdmin ? 'No roster periods' : 'No availability collection is open'}</h3>
          <p>{isAdmin ? 'Create a roster period first.' : 'Check back later — your admin will open a collection window when it\'s time to submit your availability.'}</p>
        </div>
      ) : periodLoading ? (
        <LoadingSpinner label="Loading…" />
      ) : !selectedPeriod ? null : (
        <>
          {isAdmin && (
            <AdminOverviewPanel
              period={selectedPeriod}
              statusList={statusList}
              activeWindow={activeWindow}
              allSubmissions={allSubmissions}
              onOpenWindow={handleOpenWindow}
              onSendReminder={handleSendReminder}
            />
          )}

          {showOwnForm && <WindowBanner activeWindow={activeWindow} submitted={myAvail?.submitted} />}

          {!isAdmin && !activeWindow && (
            <div style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px',
            }}>
              <Lock size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>
                Availability collection is not currently open for this period.
                {myAvail?.submitted && ' Your previous submission is shown below (read-only).'}
              </p>
            </div>
          )}

          {showOwnForm && (canEdit || myAvail?.submitted) && (
            <>
              <FullDayBlocksSection
                period={selectedPeriod}
                unavailableDates={localDates}
                halfDayDates={localHalfDays.map((h) => h.date)}
                onChange={(v) => markDirty(() => setLocalDates(v))}
                readOnly={!canEdit}
              />

              <HalfDayBlocksSection
                period={selectedPeriod}
                halfDayBlocks={localHalfDays}
                fullDayDates={localDates}
                onChange={(v) => markDirty(() => setLocalHalfDays(v))}
                readOnly={!canEdit}
              />

              <WeekendBlocksSection
                period={selectedPeriod}
                blockedWeekends={localBlockedWeekends}
                weekendRhythm={localWeekendRhythm}
                weekendMode={weekendMode}
                readOnly={!canEdit}
                assignedRhythm={myAvail?.weekend_rhythm_assigned || null}
                assignedAt={myAvail?.weekend_rhythm_assigned_at || null}
                onChange={({ blocked_weekends, weekend_rhythm, mode }) => markDirty(() => {
                  setLocalBlockedWeekends(blocked_weekends);
                  setLocalWeekendRhythm(weekend_rhythm);
                  setWeekendMode(mode);
                })}
              />

              <VacationSection
                period={selectedPeriod}
                vacationWeeks={localWeeks}
                statusByWeek={myAvail?.vacation_week_status || {}}
                balance={myAvail?.vacation_balance ?? 28}
                onChange={(v) => markDirty(() => setLocalWeeks(v))}
                readOnly={!canEdit}
                allowPast={isAdmin}
              />

              <PmsDaysSection
                period={selectedPeriod}
                prefs={localPrefs}
                onChange={(v) => markDirty(() => setLocalPrefs(v))}
                readOnly={!canEdit}
              />

              <PreferenceRanking
                ranking={visibleRanking(localPrefs.ranking, myQualified)}
                onChange={(v) => markDirty(() => setLocalPrefs((p) => ({ ...p, ranking: mergeRanking(v, p.ranking, myQualified) })))}
                readOnly={!canEdit}
                hiddenCount={myQualified ? SHIFT_TYPE_KEYS.length - myQualified.length : 0}
              />

              <div className="card" style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '10px' }}>Notes</h3>
                <textarea
                  value={localNotes}
                  onChange={(e) => markDirty(() => setLocalNotes(e.target.value))}
                  placeholder="Any additional notes for the admin…"
                  disabled={!canEdit}
                  className="form-textarea"
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  {dirty && myAvail?.submitted && (
                    <button className="btn btn-secondary" onClick={handleDiscard}>
                      Discard changes
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={saving || (!dirty && myAvail?.submitted)}
                  >
                    {saving ? 'Saving…' : myAvail?.submitted ? 'Update availability' : 'Submit availability'}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AvailabilityPage;
